import React, { useEffect } from "react";
import axios from "axios";
import Ornament from "@/components/Ornament";
import Reveal from "@/components/Reveal";
import Footer from "@/components/Footer";
import CursorAccent from "@/components/CursorAccent";
import FloatingFlorals from "@/components/FloatingFlorals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDAR_URL = `${API}/calendar/weekend.ics`;

// Records an event once per browser session (so refreshes don't inflate counts).
const trackOnce = (slug) => {
  const key = `rt_seen_${slug}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    // sessionStorage unavailable - still record
  }
  axios.post(`${API}/invites/view`, { slug }).catch(() => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  });
};

const WEEKEND = [
  {
    id: "friday",
    day: "Friday",
    date: "20th November",
    items: [
      { time: "3.30 pm onwards", text: "Guests Arrival at Foxhill Manor" },
      {
        time: "4.30 pm",
        text: "Wedding Party Rehearsal at St Peter & St Paul's Church, Blockley",
      },
      {
        time: "7 pm until late",
        text: "Dinner & Drinks at Foxhill Manor",
      },
    ],
  },
  {
    id: "saturday",
    day: "Saturday",
    date: "21st November — The Wedding Day",
    items: [
      { time: "6 am until 11.30 am", text: "Breakfast whenever and wherever" },
      {
        time: "12.15 pm",
        text: "Estate Cars for Manor Guests to St Peter & St Paul's Church, Blockley",
      },
      { time: "1 pm", text: "Wedding Ceremony at St Peter & St Paul's Church" },
      { time: "Afternoon", text: "Drinks, Wedding Breakfast & Toasts at Foxhill Manor" },
      { time: "7 pm to 9 pm", text: "Cocktail Hour and Evening Guests Arrive" },
      { time: "9.30 pm until Late", text: "First Dance & Live Band" },
      { time: "1 am", text: "Carriages" },
    ],
  },
  {
    id: "sunday",
    day: "Sunday",
    date: "22nd November",
    items: [
      { time: "6 am until 11.30 am", text: "Breakfast whenever and wherever" },
      { time: "Midday", text: "Checkout at Foxhill Manor" },
      { time: "12.30 pm until Lunch", text: "Green Room at The Fish" },
      { time: "2 pm", text: "Sunday Lunch at The Fish" },
    ],
  },
];

const Weekend = () => {
  useEffect(() => {
    document.title = "Our Wedding Weekend — Rebecca & Tom";
    window.scrollTo(0, 0);
    trackOnce("weekend");
    return () => {
      document.title = "Rebecca & Tom — 21st November 2026";
    };
  }, []);

  return (
    <div className="page weekend-page">
      <CursorAccent />
      <FloatingFlorals />
      <div className="frame">
        <nav className="nav" data-testid="weekend-nav">
          <div className="nav-inner">
            <a href="/" className="nav-monogram" aria-label="Home">
              R <span className="amp">&amp;</span> T
            </a>
            <ul className="nav-links weekend-nav-links">
              <li>
                <a href="/" data-testid="weekend-nav-home">
                  Home
                </a>
              </li>
              <li>
                <a href="/#rsvp" data-testid="weekend-nav-rsvp">
                  RSVP
                </a>
              </li>
            </ul>
          </div>
        </nav>

        <div className="weekend-hero" data-testid="weekend-hero">
          <img
            src="/images/manor.png"
            alt="Watercolour of Foxhill Manor"
            className="weekend-hero-img"
            loading="eager"
          />
          <div className="weekend-hero-fade" />
        </div>

        <section id="weekend" className="section weekend" data-testid="section-weekend">
          <Ornament />
          <Reveal as="h1" className="script-h2 weekend-title">
            Our Wedding Weekend
          </Reveal>
          <Reveal as="p" className="muted center">
            Friday 20th — Sunday 22nd November 2026
          </Reveal>
          <Reveal className="center weekend-cal">
            <a
              href={CALENDAR_URL}
              download="rebecca-and-tom-wedding-weekend.ics"
              onClick={() => trackOnce("weekend-calendar")}
              className="btn primary"
              data-testid="add-weekend-to-calendar"
            >
              Add the weekend to my calendar
            </a>
            <p className="weekend-cal-hint">
              Saves every event with times and locations to Apple, Google or Outlook calendar.
            </p>
          </Reveal>

          <div className="weekend-days">
            {WEEKEND.map((d, di) => (
              <div className="weekend-day" key={d.id} data-testid={`weekend-${d.id}`}>
                <Reveal className="weekend-day-head" delay={di * 60}>
                  <span className="weekend-day-name">{d.day}</span>
                  <span className="weekend-day-date">{d.date}</span>
                </Reveal>
                <ul className="timeline">
                  {d.items.map((t, i) => (
                    <Reveal as="li" key={`${d.id}-${t.time}`} delay={i * 80}>
                      <span className="t">{t.time}</span>
                      <span className="dash">—</span>
                      <span className="desc">{t.text}</span>
                    </Reveal>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <Reveal as="p" className="muted center subtle">
            We can&apos;t wait to celebrate the whole weekend with you.
          </Reveal>
          <Reveal className="center weekend-back">
            <a
              href={CALENDAR_URL}
              download="rebecca-and-tom-wedding-weekend.ics"
              onClick={() => trackOnce("weekend-calendar")}
              className="btn ghost"
              data-testid="add-weekend-to-calendar-bottom"
            >
              Add to calendar
            </a>
          </Reveal>
          <Reveal className="center weekend-back">
            <a href="/" className="textlink" data-testid="weekend-back-home">
              ← Back to the invitation
            </a>
          </Reveal>
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default Weekend;
